import { useListApps } from "@package/api-client/hooks/appsController";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { LucideIcon } from "lucide-react";
import { ArrowRight, KeyRound, Mail, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/_auth/app/_app/org/$orgId/app/$appId/")({
	component: AppDashboard,
});

interface LauncherEntry {
	key: string;
	to:
		| "/app/org/$orgId/app/$appId/messages"
		| "/app/org/$orgId/app/$appId/sender-identities"
		| "/app/org/$orgId/app/$appId/api-keys";
	icon: LucideIcon;
	hint: string;
	primary?: boolean;
}

const ENTRIES: LauncherEntry[] = [
	{
		key: "messages",
		to: "/app/org/$orgId/app/$appId/messages",
		icon: Mail,
		hint: "/messages",
		primary: true,
	},
	{
		key: "senderIdentities",
		to: "/app/org/$orgId/app/$appId/sender-identities",
		icon: ShieldCheck,
		hint: "/sender-identities",
	},
	{ key: "apiKeys", to: "/app/org/$orgId/app/$appId/api-keys", icon: KeyRound, hint: "/api-keys" },
];

function AppDashboard() {
	const { t } = useTranslation();
	const { orgId, appId } = Route.useParams();
	const { data: apps } = useListApps(orgId);
	const app = apps?.find((a) => a.id === appId);

	return (
		<div className="h-full overflow-auto">
			<div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto">
				{/* Header */}
				<header className="mb-8">
					<p className="font-mono text-xs uppercase tracking-wide text-base-content/55">
						{t("nav.dashboard")}
					</p>
					<h1
						data-testid="dashboard-title"
						className="mt-1 text-2xl sm:text-3xl font-semibold tracking-tight text-base-content"
					>
						{app?.name ?? t("nav.dashboard")}
					</h1>
					<p className="mt-2 font-mono text-xs text-base-content/50">{appId}</p>
				</header>

				{/* Launcher: a flat, dense list. The send log leads. */}
				<nav className="rounded-box border border-base-300 bg-base-100 overflow-hidden divide-y divide-base-300">
					{ENTRIES.map((entry) => (
						<Link
							key={entry.key}
							to={entry.to}
							params={{ orgId, appId }}
							data-testid={`dashboard-action-${entry.key}`}
							className="group flex items-center gap-4 px-4 py-3.5 transition-colors duration-150 hover:bg-base-200"
						>
							<entry.icon className="h-5 w-5 shrink-0 text-base-content/60 group-hover:text-base-content transition-colors duration-150" />
							<div className="flex-1 min-w-0">
								<div className="flex items-center gap-2">
									{entry.primary && (
										<span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />
									)}
									<span className="font-medium text-sm text-base-content">
										{t(`nav.${entry.key}`)}
									</span>
								</div>
								<span className="font-mono text-xs text-base-content/45">{entry.hint}</span>
							</div>
							<ArrowRight className="h-4 w-4 shrink-0 text-base-content/25 group-hover:text-base-content/60 transition-colors duration-150" />
						</Link>
					))}
				</nav>
			</div>
		</div>
	);
}
